'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import FormattedMessage from 'I18n/components/FormattedMessage';
import NestedNav from 'ecosystem-marketplace-core/components/nav/NestedNav';
import * as Tier from 'ecosystem-marketplace-core/constants/groupTags/Tier';
import { getTierI18nKey } from 'ecosystem-marketplace-core/utils/i18n';
import { TierGroup, ORDER as tiersOrder } from '../../../../constants/tiers';
import NestedNavCheckboxItem from '../inputs/NestedNavCheckboxItem';

const TierGroupCheckboxItem = (_ref) => {
  let {
    disabled,
    groupKey,
    groupTiers,
    onChange,
    selectedTiers
  } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, ["disabled", "groupKey", "groupTiers", "onChange", "selectedTiers"]);

  const allSelected = useMemo(() => groupTiers.every(tier => selectedTiers.includes(tier)), [groupTiers, selectedTiers]);
  const someSelected = useMemo(() => groupTiers.some(tier => selectedTiers.includes(tier)), [groupTiers, selectedTiers]);
  const renderedChildTiers = useMemo(() => groupTiers.map(tier => /*#__PURE__*/_jsx(NestedNavCheckboxItem, {
    checkboxProps: {
      checked: selectedTiers.includes(tier),
      disabled,
      onChange: ({
        target: {
          checked
        }
      }) => {
        onChange(checked, [tier]);
      }
    },
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: getTierI18nKey(tier)
    })
  }, tier)).toArray(), [disabled, groupTiers, onChange, selectedTiers]);
  return /*#__PURE__*/_jsx(NestedNavCheckboxItem, Object.assign({}, rest, {
    checkboxProps: {
      checked: allSelected,
      disabled,
      indeterminate: someSelected,
      onChange: ({
        target: {
          checked
        }
      }) => {
        onChange(checked, groupTiers.toArray());
      },
      value: groupKey
    },
    defaultOpen: allSelected || someSelected,
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: `appMarketplace.storefront.filters.compatibility.items.${groupKey}`
    }),
    children: renderedChildTiers
  }));
};

const CompatibilityToggleGroup = ({
  compatible,
  isCustomer,
  onCompatibleChange,
  onTiersChange,
  selectedTiers
}) => {
  const handleTiersChange = useCallback((checked, values) => {
    onTiersChange(checked ? selectedTiers.union(values) : selectedTiers.subtract(values));
  }, [onTiersChange, selectedTiers]);
  const renderedTierItems = useMemo(() => {
    return tiersOrder.map(tierOrTierGroup => {
      const disabled = !!compatible;

      if (tierOrTierGroup instanceof TierGroup) {
        const {
          key: groupKey,
          tiers: groupTiers
        } = tierOrTierGroup;
        return /*#__PURE__*/_jsx(TierGroupCheckboxItem, {
          disabled: disabled,
          groupKey: groupKey,
          groupTiers: groupTiers,
          onChange: handleTiersChange,
          selectedTiers: selectedTiers
        }, groupKey);
      }

      return /*#__PURE__*/_jsx(NestedNavCheckboxItem, {
        checkboxProps: {
          checked: selectedTiers.includes(tierOrTierGroup),
          disabled,
          onChange: ({
            target: {
              checked
            }
          }) => {
            handleTiersChange(checked, [tierOrTierGroup]);
          }
        },
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: getTierI18nKey(tierOrTierGroup)
        })
      }, tierOrTierGroup);
    });
  }, [compatible, handleTiersChange, selectedTiers]);
  return /*#__PURE__*/_jsxs(NestedNav, {
    className: "m-bottom-0",
    children: [isCustomer && /*#__PURE__*/_jsx(NestedNavCheckboxItem, {
      checkboxProps: {
        checked: compatible,
        onChange: ({
          target: {
            checked
          }
        }) => {
          onCompatibleChange(checked);
        }
      },
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "appMarketplace.storefront.filters.compatibility.items.compatible"
      })
    }), renderedTierItems]
  });
};

CompatibilityToggleGroup.propTypes = {
  compatible: PropTypes.bool,
  isCustomer: PropTypes.bool.isRequired,
  onCompatibleChange: PropTypes.func.isRequired,
  onTiersChange: PropTypes.func.isRequired,
  selectedTiers: ImmutablePropTypes.iterableOf(PropTypes.oneOf(Object.values(Tier))).isRequired
};
export default CompatibilityToggleGroup;